import { motion } from "framer-motion";
import { Globe, Sparkles, ShieldCheck, Coins, Rocket, Mail, ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { SUPPORT_EMAIL } from "../data";
import usePageMeta from "../hooks/usePageMeta";

const PILLARS = [
  { icon: <Globe className="w-6 h-6" />, t: "A new category", d: "Not another feed. MapCast connects people through places, cultures, and real-life experiences — a live, interactive map of humanity instead of an algorithmic timeline." },
  { icon: <Sparkles className="w-6 h-6" />, t: "AI at the core", d: "Real-time translation, live subtitles, and voice dubbing remove language barriers; AI context makes every stream more meaningful. AI is the foundation, not a feature." },
  { icon: <ShieldCheck className="w-6 h-6" />, t: "Safety as a moat", d: "Face & plate anonymization, sensitive-location protection, and content moderation — designed to be one of the safest real-time platforms and a trusted, regulator-ready brand." },
  { icon: <Coins className="w-6 h-6" />, t: "Monetization built-in", d: "Pay-per-minute viewing, tips, and location bounties create wallet-to-wallet value from day one — an economy that rewards both creators and the platform." },
];

const STAGES = [
  { n: "Stage 1", t: "Live now — on iOS & Android", d: "Live map discovery, real-time broadcasting, pay-per-minute, tips, and bounties. Available on the App Store & Google Play." },
  { n: "Stage 2", t: "AI safety & language", d: "Face-blur, privacy zones, live translation, subtitles, and voice dubbing across languages." },
  { n: "Stage 3", t: "Trust & scale", d: "Verified MapCasters, geofencing, crowd & event detection, and advanced content safety at global scale." },
];

const DECK_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("MapCast — Investor deck request")}&body=${encodeURIComponent("Hi MapCast team,\n\nI'd love to learn more about the raise and receive the investor deck.\n\nName:\nFund / Company:\nRole:\n\nThanks!")}`;

export default function Investors() {
  usePageMeta({
    title: "Investors — MapCast",
    description: "Back the world's AI-powered location platform. MapCast connects humanity through places — live video anchored to the world map.",
    ogTitle: "Back the world's AI-powered location platform.",
    ogDescription: "MapCast connects humanity through places — live video anchored to the world map, with pay-per-minute, tips, and bounties built in.",
    ogImage: "https://mapcast.cc/assets/og-investors.jpg",
    ogUrl: "https://mapcast.cc/investors",
  });
  return (
    <div className="relative bg-mc-bg min-h-screen pt-36 pb-32 px-6 lg:px-10 spotlight">
      <div className="mx-auto max-w-5xl">
        {/* hero */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-mc-pink mb-5">For investors</p>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
            Back the world's AI-powered <span className="text-gradient">location platform.</span>
          </h1>
          <p className="mt-7 text-lg md:text-xl text-mc-muted font-light leading-relaxed max-w-2xl">
            MapCast connects humanity through places — a live, AI-powered map where anyone can explore
            the world through the eyes of the people who live there. We're building the most trusted
            real-time location platform, live now on the App Store and Google Play.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a href={DECK_MAILTO} className="inline-flex items-center gap-2 rounded-full bg-white text-mc-bg font-semibold px-7 h-14 hover:scale-105 transition-transform duration-300" data-testid="investors-request-deck">
              <Mail className="w-5 h-5" /> Request the investor deck
            </a>
            <a href="#opportunity" className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white font-semibold px-7 h-14 hover:bg-white/10 transition-colors" data-testid="investors-explore">
              Why now <ArrowUpRight className="w-5 h-5" />
            </a>
          </div>
        </motion.div>

        {/* pillars */}
        <div id="opportunity" className="mt-20 grid md:grid-cols-2 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.08}>
              <div className="glass rounded-3xl p-8 h-full">
                <span className="inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br from-mc-violet/30 to-mc-pink/30 border border-white/10 items-center justify-center text-mc-pink">{p.icon}</span>
                <h3 className="font-display font-bold text-2xl text-white mt-5">{p.t}</h3>
                <p className="mt-3 text-mc-muted font-light leading-relaxed">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* roadmap */}
        <div className="mt-24">
          <Reveal>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tighter text-white mb-4">Where we are</h2>
            <p className="text-mc-muted font-light text-lg max-w-2xl">A staged path from a working preview to a global, AI-safe, trusted platform.</p>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {STAGES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="relative glass rounded-3xl p-7 h-full">
                  <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-mc-pink">
                    <Rocket className="w-4 h-4" /> {s.n}
                  </span>
                  <h4 className="font-display font-bold text-xl text-white mt-4">{s.t}</h4>
                  <p className="mt-3 text-sm text-mc-muted font-light leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Reveal className="mt-24">
          <div className="relative rounded-[2.5rem] overflow-hidden border border-white/10 spotlight p-10 md:p-16 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-mc-violet/20 via-transparent to-mc-pink/20" aria-hidden />
            <div className="relative">
              <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tighter text-white leading-[0.98]">Let's build the map of humanity.</h2>
              <p className="mt-5 text-mc-muted font-light max-w-xl mx-auto">
                We share detailed materials with qualified investors on request. Reach out and we'll send the deck.
              </p>
              <a href={DECK_MAILTO} className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-mc-bg font-semibold px-7 h-14 hover:scale-105 transition-transform duration-300" data-testid="investors-cta-deck">
                <Mail className="w-5 h-5" /> {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
        </Reveal>

        <p className="mt-10 text-xs text-mc-muted/80 font-light leading-relaxed max-w-3xl">
          This page is provided for information purposes only and does not constitute an offer to sell, or a
          solicitation of an offer to buy, any securities. Any potential investment would be made only pursuant
          to definitive documentation and in compliance with applicable law. MapCast BV · Herengracht 320, 1016 CE Amsterdam.
        </p>
      </div>
    </div>
  );
}
